// Capability detection — decides the default Smart Mode behaviour
// based on what the browser + device can actually handle.
//
// auto-load:  WebGPU + ≥4 GB device memory + wifi/4g + not mobile
//             → silently start the model download in the background
// opt-in:     basic compatibility but limited bandwidth or RAM
//             → require explicit user click before downloading
// unsupported: no WebGPU at all
//             → hide Smart Mode entirely; Path A only

export type Capability = "auto-load" | "opt-in" | "unsupported";

export type CapabilityReport = {
  decision: Capability;
  reasons: string[];        // human-readable explanation
  webgpu: boolean;
  deviceMemoryGB: number | null;
  effectiveType: string | null;
  isMobile: boolean;
};

// Narrow window types we touch
type NavigatorWithExtras = Navigator & {
  gpu?: unknown;
  deviceMemory?: number;
  connection?: { effectiveType?: string; downlink?: number };
  userAgentData?: { mobile?: boolean };
};

export function detectCapability(): CapabilityReport {
  if (typeof navigator === "undefined") {
    return {
      decision: "unsupported",
      reasons: ["server-side render"],
      webgpu: false,
      deviceMemoryGB: null,
      effectiveType: null,
      isMobile: false,
    };
  }

  const nav = navigator as NavigatorWithExtras;
  const webgpu = !!nav.gpu;
  const deviceMemoryGB = typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
  const effectiveType = nav.connection?.effectiveType ?? null;
  const isMobile =
    nav.userAgentData?.mobile === true ||
    /android|iphone|ipad|ipod/i.test(nav.userAgent ?? "");

  const reasons: string[] = [];
  if (!webgpu) reasons.push("no WebGPU");
  if (deviceMemoryGB !== null && deviceMemoryGB < 4) reasons.push(`only ${deviceMemoryGB} GB RAM`);
  if (effectiveType && /^(slow-)?[23]g$/.test(effectiveType)) reasons.push(`slow connection (${effectiveType})`);
  if (isMobile) reasons.push("mobile device");

  let decision: Capability;
  if (!webgpu) {
    decision = "unsupported";
  } else if (isMobile || (deviceMemoryGB !== null && deviceMemoryGB < 4) || (effectiveType && /^(slow-)?[23]g$/.test(effectiveType))) {
    decision = "opt-in";
    if (reasons.length === 0) reasons.push("conservative default");
  } else {
    decision = "auto-load";
    reasons.push("WebGPU + capable device + ok connection");
  }

  return { decision, reasons, webgpu, deviceMemoryGB, effectiveType, isMobile };
}
